/**
 * Keep the theme header cart count in sync with third-party cart mutations.
 *
 * The slide-cart app (PickyStory / Amplify Bundles + Samita Wholesale) adds and
 * removes line items via the Shopify AJAX Cart API (/cart/add|change|update|clear)
 * but does NOT re-render the theme's `#cart-icon-bubble` / `#cart-icon-bubble-mobile`
 * elements, so the header count goes stale (most visibly when removing an item).
 *
 * We watch for any cart-mutating fetch/XHR from any script and, once it completes,
 * refresh the header count from /cart.js. The theme's own cart operations already
 * update the bubble; running this in addition is idempotent and harmless.
 */
(function () {
	const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js|\b)/i;
	const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

	let timer = null;
	const scheduleRefresh = () => {
		clearTimeout(timer);
		timer = setTimeout(refresh, 250); // debounce bursts of cart calls
	};

	const refresh = () => {
		fetch(`${ROOT}cart.js`, { headers: { Accept: "application/json" } })
			.then((r) => r.json())
			.then((cart) => updateCount(cart.item_count))
			.catch(() => {});
	};

	const updateCount = (count) => {
		// Desktop bubble(s) — a bare "(N)" span, hidden when the cart is empty.
		document.querySelectorAll("#cart-icon-bubble").forEach((el) => {
			el.textContent = `(${count})`;
			el.classList.toggle("hidden", count === 0);
		});

		// Mobile bubble — a count chip that only exists while the cart is non-empty.
		document.querySelectorAll("#cart-icon-bubble-mobile").forEach((link) => {
			let bubble = link.querySelector(".header-mobile__cart-count-bubble");
			if (count === 0) {
				if (bubble) bubble.remove();
				return;
			}
			if (!bubble) {
				bubble = document.createElement("div");
				bubble.className = "header-mobile__cart-count-bubble";
				bubble.innerHTML = '<span aria-hidden="true"></span>';
				link.appendChild(bubble);
			}
			let span = bubble.querySelector('span[aria-hidden="true"]');
			if (!span) {
				span = document.createElement("span");
				span.setAttribute("aria-hidden", "true");
				bubble.prepend(span);
			}
			span.textContent = count;
		});
	};

	// --- Detect cart-mutating AJAX from any source ---------------------------

	const origFetch = window.fetch;
	if (typeof origFetch === "function") {
		window.fetch = function (...args) {
			const input = args[0];
			const url = typeof input === "string" ? input : input && input.url;
			const isCart = url && CART_MUTATION.test(String(url));
			const result = origFetch.apply(this, args);
			if (isCart && result && typeof result.then === "function") {
				result.then(scheduleRefresh).catch(() => {});
			}
			return result;
		};
	}

	const origOpen = XMLHttpRequest.prototype.open;
	XMLHttpRequest.prototype.open = function (method, url) {
		this.__cartMutation = url && CART_MUTATION.test(String(url));
		return origOpen.apply(this, arguments);
	};
	const origSend = XMLHttpRequest.prototype.send;
	XMLHttpRequest.prototype.send = function () {
		if (this.__cartMutation) this.addEventListener("loadend", scheduleRefresh);
		return origSend.apply(this, arguments);
	};
})();
